import TabPageHeading from '../components/Layout/TabPageHeading';
import PlannerDiscover from '../components/planner/PlannerDiscover';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CyclingEvent, EventCommunity, PlannerView, communityGroups, plannerEventsAfterChange, plannerView, splitFavorites } from '@radevents/shared';
import { CommunityManager, useCommunity } from '../components/community/Community';
import { FreundeKopf, FreundeLeer } from '../components/community/FreundeKopf';
import { X } from 'lucide-react';
import EventPlannerView from '../components/planner/EventPlannerView';
import eventService from '../services/eventService';
import communityService from '../services/communityService';
import { useAuth } from '../contexts/AuthContext';
import { clearListCache } from '../hooks/useListCache';
import PublicHeader from '../components/Layout/PublicHeader';
import PlannerEventCard from '../components/planner/PlannerEventCard';
import PlannerAgenda from '../components/planner/PlannerAgenda';
import { useWischen } from '../hooks/useWischen';
import MeineEventsGast from '../components/planner/MeineEventsGast';

export default function Favorites() {
  const { user, isAuthenticated, isLoading } = useAuth();
  // Ohne Konto keine Umleitung zur Anmeldung mehr: der Tab erklärt, was Vormerken bringt (MeineEventsGast).
  if (!isLoading && !isAuthenticated) {
    return <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />
      <main className="planner-page tab-page-content"><TabPageHeading unsichtbar>Meine Events</TabPageHeading><MeineEventsGast /></main>
    </div>;
  }
  return <PlannerAccount key={user?.id} />;
}
function PlannerAccount() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = plannerView(params.get('view'));
  // Beide Ansichten bleiben im Speicher (30.10.2026): beim Wechsel steht sofort der letzte Stand,
  // nachgeladen wird im Hintergrund. Vorher leerte jeder Wechsel die Liste und zeigte „Events
  // werden geladen“, das flackerte. `undefined` heißt: für diese Ansicht noch nie geladen.
  const [listen, setListen] = useState<Record<PlannerView, CyclingEvent[] | undefined>>({ mine: undefined, friends: undefined });
  const events = listen[view] ?? [];
  const [error, setError] = useState('');
  const loading = listen[view] === undefined && !error;
  const setEvents = (update: (list: CyclingEvent[]) => CyclingEvent[]) =>
    setListen(l => ({ ...l, [view]: update(l[view] ?? []) }));
  const [showPast, setShowPast] = useState(false);
  // `?verwalten=1` klappt die Freundesliste auf, etwa nach einer angenommenen Einladung.
  const [manageFriends, setManageFriends] = useState(() => params.get('verwalten') === '1');
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision(n => n + 1), []);
  /** Aus welcher Richtung die neue Ansicht hereinkommt, nur für die Bewegung beim Wechsel. */
  const [richtung, setRichtung] = useState<'links' | 'rechts' | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) { navigate('/login', { state: { from: { pathname: '/favorites', search: params.toString() ? `?${params}` : '' } }, replace: true }); return; }
    let active = true; setError('');
    const laden = (v: PlannerView) => communityService.planning(v).then(list => { if (active) setListen(l => ({ ...l, [v]: list })); });
    laden(view)
      .catch(() => { if (active) setError('Deine Eventliste konnte nicht geladen werden.'); })
      // Die andere Ansicht gleich mitholen: dann ist schon der erste Wechsel ohne Wartezeit.
      .then(() => laden(view === 'mine' ? 'friends' : 'mine')).catch(() => {});
    return () => { active = false; };
  // The URL view is the only query parameter used by this loader.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, revision, isLoading, isAuthenticated, navigate]);
  // Freunde einmal für Leiste, Anfragen und Verwaltung (useCommunity); eine Änderung dort lädt
  // die Events der Freunde neu.
  const community = useCommunity(reload);
  const gruppen = communityGroups(community.data?.people ?? []);
  const hatFreunde = gruppen.friends.length + gruppen.incoming.length + gruppen.outgoing.length > 0;
  /** Verwaltung über „Einladen“ geöffnet: dann steht die Suche samt Einladungslink gleich offen. */
  const [einladen, setEinladen] = useState(false);
  const verwaltungOeffnen = (mitEinladen: boolean) => { setEinladen(mitEinladen); setManageFriends(true); };
  const wechseln = (v: PlannerView) => {
    if (v === view) return;
    setRichtung(v === 'friends' ? 'rechts' : 'links');
    setShowPast(false);
    setParams(v === 'mine' ? {} : { view: v });
  };
  // Am Handy zwischen den Reitern wischen: nach links zu Freunde, nach rechts zurück.
  const wischen = useWischen(() => wechseln('friends'), () => wechseln('mine'));
  const { upcoming, past } = splitFavorites(events);
  const toggleBookmark = async (event: CyclingEvent) => {
    if (event.favorited) await eventService.removeFavorite(event.id); else await eventService.addFavorite(event.id);
    clearListCache('landing'); clearListCache('favorites');
    setEvents(list => plannerEventsAfterChange(list, event.id, { favorited: !event.favorited }, view));
  };
  const participation = async (event: CyclingEvent, going: boolean) => {
    const data: EventCommunity = await communityService.saveInterest(event.id, going ? { status: 'GOING', visibility: 'FRIENDS' } : null);
    setEvents(list => plannerEventsAfterChange(list, event.id, { participating: !!data.interest }, view));
    clearListCache('landing'); clearListCache('favorites');
  };
  const card = (event: CyclingEvent) => <PlannerEventCard key={event.id} event={event} onBookmark={() => toggleBookmark(event)} onParticipation={going => participation(event, going)} />;
  const agenda = (list: CyclingEvent[], mitAbstand = false) => <PlannerAgenda events={list} renderCard={card} mitAbstand={mitAbstand} />;
  return <div style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
    <PublicHeader />
    <main className="planner-page tab-page-content" {...wischen}>
      <EventPlannerView view={view} onView={wechseln} richtung={richtung} loading={loading} error={error} empty={upcoming.length === 0} onRetry={reload} cards={agenda(upcoming, view === 'mine')}
        friends={<div className="planner-friends">
          {community.data && hatFreunde && <FreundeKopf data={community.data} busy={community.busy} onAction={community.action}
            verwaltenOffen={manageFriends} onEinladen={() => verwaltungOeffnen(true)} onVerwalten={() => manageFriends ? setManageFriends(false) : verwaltungOeffnen(false)} />}
          {manageFriends && <div className="fk-panel ui-aufklappen">
            <div className="fk-panel-kopf"><h2>Freunde verwalten</h2><button type="button" className="vf-schliessen" aria-label="Schließen" onClick={() => setManageFriends(false)}><X size={18} aria-hidden /></button></div>
            <CommunityManager c={community} startAdding={einladen} />
          </div>}
        </div>}
        leer={view === 'friends' && community.data
          ? (gruppen.friends.length === 0
            ? (manageFriends ? null : <FreundeLeer inviteUrl={community.data.profile?.inviteUrl} onSuchen={() => verwaltungOeffnen(true)} />)
            : <div className="planner-empty"><h2>Noch nichts vorgemerkt</h2><p>Sobald deine Freunde ein Event vormerken oder zusagen, steht es hier.</p></div>)
          : undefined}
        past={past.length > 0 && <div className="planner-past"><label><input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} /> Vergangene Events zeigen ({past.length})</label>{showPast && <div className="planner-agenda">{agenda(past)}</div>}</div>} />
      <PlannerDiscover />
    </main>
  </div>;
}
